<template>
  <div class="lock-demon-tower">
      <!-- PC端显示 -->
      <div v-if="!isMobile" class="pc-version">
          <div class="header-section">
              <Select v-model="mapName" style="width: 50%">
                  <Option v-for="item in mapList" :value="item.mapName" :name="item.mapId" :key="item.mapId">
                      {{ item.mapName }} {{item.minLv}}-{{item.maxLv}}
                  </Option>
              </Select>
              <Button v-if="!isAutoBattle" @click.prevent="startTheBattle" size="small" type="info" ghost>
                  开始战斗
              </Button>
              <Button v-if="isAutoBattle" @click.prevent="stopAutoBattle" size="small" type="error">
                  停止 ({{ autoBattleCountdown }}s)
              </Button>
              <Button v-if="!isAutoBattle" @click.prevent="startAutoBattle" size="small" type="success" ghost>
                  自动战斗
              </Button>
              <ButtonGroup style="margin-left: 8px;">
                  <Button
                      v-for="speed in replaySpeedOptions"
                      :key="'speed-' + speed"
                      size="small"
                      :type="replaySpeed === speed ? 'primary' : 'default'"
                      @click="setReplaySpeed(speed)"
                  >
                      {{ speed === 'all' ? '全部' : speed + 'x' }}
                  </Button>
              </ButtonGroup>
          </div>
      </div>

      <!-- 移动端显示 -->
      <div v-if="isMobile" class="mobile-version">
          <!-- 地图选择按钮 -->
          <div class="mobile-header">
              <Button @click="showMapModal = true" long type="primary" icon="ios-map">
                  选择地图
              </Button>
              <Button v-if="!isAutoBattle" @click.prevent="startTheBattle" long type="success" icon="ios-flame" style="margin-top: 10px">
                  开始战斗
              </Button>
              <Button v-if="isAutoBattle" @click.prevent="stopAutoBattle" long type="error" icon="ios-close-circle" style="margin-top: 10px">
                  停止 ({{ autoBattleCountdown }}s)
              </Button>
              <Button v-if="!isAutoBattle" @click.prevent="startAutoBattle" long type="primary" icon="ios-play" style="margin-top: 10px">
                  自动战斗
              </Button>
              <ButtonGroup style="margin-top: 10px; display: flex;">
                  <Button
                      v-for="speed in replaySpeedOptions"
                      :key="'speed-m-' + speed"
                      size="small"
                      :type="replaySpeed === speed ? 'primary' : 'default'"
                      @click="setReplaySpeed(speed)"
                      style="flex: 1;"
                  >
                      {{ speed === 'all' ? '全部' : speed + 'x' }}
                  </Button>
              </ButtonGroup>
          </div>

          <!-- 当前选择的地图显示 -->
          <div v-if="mapName" class="current-map">
              <Tag color="primary">
                  <Icon type="ios-pin" />
                  当前选择: {{ mapName }}
              </Tag>
          </div>

          <!-- 地图弹窗 -->
          <Modal
              v-model="showMapModal"
              title="选择地图"
              z-index="1200"
              :fullscreen="true"
              :footer-hide="true"
              class-name="map-selection-modal"
          >
              <div class="map-modal-content">
                  <div class="map-grid">
                      <div
                          v-for="item in mapList"
                          :key="item.mapId"
                          class="map-item"
                          @click="selectMap(item)"
                          :class="{ 'selected': item.mapName === mapName }"
                      >
                          <div class="map-icon">
                              <Icon type="ios-cube" size="24" />
                          </div>
                          <div class="map-info">
                              <h4>{{ item.mapName }}</h4>
                              <p>推荐等级: {{ item.minLv }}-{{ item.maxLv }}</p>
                          </div>
                          <div v-if="item.mapName === mapName" class="selected-indicator">
                              <Icon type="ios-checkmark-circle" color="#19be6b" />
                          </div>
                      </div>
                  </div>

                  <div class="modal-footer">
                      <Button type="primary" long @click="showMapModal = false" style="margin-top: 20px">
                          确定选择
                      </Button>
                  </div>
              </div>
          </Modal>
      </div>

      <div class="tower-status-bar">
          <Tag v-if="mapName" color="primary">当前挑战：{{ mapName }}</Tag>
          <Tag v-if="towerResultText" :color="towerRewardGranted ? 'success' : 'blue'">
              {{ towerResultText }}
          </Tag>
          <Tag v-if="towerBattleDurationText" color="cyan">
              {{ towerBattleDurationText }}
          </Tag>
      </div>

      <!-- 战斗统计 DPS -->
      <div v-if="battleTotal && dpsStats.totalRounds > 0" class="battle-stats">
          <div class="dps-summary-bar">
              <Tag color="blue">总回合数 {{ dpsStats.totalRounds }}</Tag>
              <Tag color="green">我方总伤害 {{ formatNumber(dpsStats.allyTotalDamage) }}</Tag>
              <Tag color="orange">敌方总伤害 {{ formatNumber(dpsStats.enemyTotalDamage) }}</Tag>
          </div>
          <div class="dps-section">
              <div class="dps-section-title">我方</div>
              <table class="dps-table">
                  <thead>
                      <tr>
                          <th>角色</th>
                          <th>总伤害</th>
                          <th>普攻伤害</th>
                          <th>技能伤害</th>
                          <th>反击伤害</th>
                          <th>持续伤害</th>
                          <th>治疗</th>
                          <th>护盾</th>
                          <th>吸血</th>
                          <th>暴击次数</th>
                          <th>连击伤害</th>
                          <th>攻击次数</th>
                          <th>均伤/次</th>
                          <th>DPS</th>
                          <th>承伤</th>
                          <th>技能</th>
                      </tr>
                  </thead>
                  <tbody>
                      <template v-for="unit in dpsStats.allies">
                          <tr :key="'ally-' + unit.name" class="ally-row">
                              <td class="unit-name" :class="{ 'zero-dmg': unit.totalDamage === 0 }">{{ unit.name }}</td>
                              <td class="num">{{ formatNumber(unit.totalDamage) }}</td>
                              <td class="num">{{ formatNumber(unit.normalDamage) }}</td>
                              <td class="num">{{ formatNumber(unit.skillDamage) }}</td>
                              <td class="num">{{ formatNumber(unit.counterDamage) }}</td>
                              <td class="num">{{ formatNumber(unit.dotDamage) }}</td>
                              <td class="num heal">{{ formatNumber(unit.healing) }}</td>
                              <td class="num">{{ formatNumber(unit.shield) }}</td>
                              <td class="num heal">{{ formatNumber(unit.lifeSteal) }}</td>
                              <td class="num">{{ unit.critCount }}</td>
                              <td class="num">{{ formatNumber(unit.comboDamage) }}</td>
                              <td class="num">{{ unit.attackCount }}</td>
                              <td class="num">{{ unit.avgDamage }}</td>
                              <td class="num dps-val">{{ unit.dps }}</td>
                              <td class="num">{{ formatNumber(unit.damageReceived) }}</td>
                              <td>
                                  <span
                                      v-if="Object.keys(unit.skills).length"
                                      class="skill-toggle"
                                      @click="toggleSkillExpand(unit.name)"
                                  >{{ isSkillExpanded(unit.name) ? '收起' : Object.keys(unit.skills).length + '个' }}</span>
                                  <span v-else class="no-skill">-</span>
                              </td>
                          </tr>
                          <tr v-if="isSkillExpanded(unit.name) && Object.keys(unit.skills).length" :key="'ally-skill-' + unit.name" class="skill-detail-row">
                              <td colspan="16">
                                  <table class="skill-sub-table">
                                      <thead>
                                          <tr>
                                              <th>技能名</th>
                                              <th>技能伤害</th>
                                              <th>连击伤害</th>
                                              <th>总伤害</th>
                                              <th>吸血</th>
                                              <th>恢复</th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          <tr v-for="(sk, skName) in unit.skills" :key="skName">
                                              <td class="skill-name-cell">{{ skName }}</td>
                                              <td class="num">{{ formatNumber(sk.damage - sk.comboDamage) }}</td>
                                              <td class="num">{{ formatNumber(sk.comboDamage) }}</td>
                                              <td class="num">{{ formatNumber(sk.damage) }}</td>
                                              <td class="num heal">{{ formatNumber(sk.lifeSteal) }}</td>
                                              <td class="num heal">{{ formatNumber(sk.healing) }}</td>
                                          </tr>
                                      </tbody>
                                  </table>
                              </td>
                          </tr>
                      </template>
                      <tr v-if="dpsStats.allies.length === 0">
                          <td colspan="16" class="empty-cell">无我方单位数据</td>
                      </tr>
                  </tbody>
              </table>
          </div>
          <div class="dps-section">
              <div class="dps-section-title">敌方</div>
              <table class="dps-table">
                  <thead>
                      <tr>
                          <th>角色</th>
                          <th>总伤害</th>
                          <th>普攻伤害</th>
                          <th>技能伤害</th>
                          <th>反击伤害</th>
                          <th>持续伤害</th>
                          <th>治疗</th>
                          <th>护盾</th>
                          <th>吸血</th>
                          <th>暴击次数</th>
                          <th>连击伤害</th>
                          <th>攻击次数</th>
                          <th>均伤/次</th>
                          <th>DPS</th>
                          <th>承伤</th>
                          <th>技能</th>
                      </tr>
                  </thead>
                  <tbody>
                      <template v-for="unit in dpsStats.enemies">
                          <tr :key="'enemy-' + unit.name" class="enemy-row">
                              <td class="unit-name" :class="{ 'zero-dmg': unit.totalDamage === 0 }">{{ unit.name }}</td>
                              <td class="num">{{ formatNumber(unit.totalDamage) }}</td>
                              <td class="num">{{ formatNumber(unit.normalDamage) }}</td>
                              <td class="num">{{ formatNumber(unit.skillDamage) }}</td>
                              <td class="num">{{ formatNumber(unit.counterDamage) }}</td>
                              <td class="num">{{ formatNumber(unit.dotDamage) }}</td>
                              <td class="num heal">{{ formatNumber(unit.healing) }}</td>
                              <td class="num">{{ formatNumber(unit.shield) }}</td>
                              <td class="num heal">{{ formatNumber(unit.lifeSteal) }}</td>
                              <td class="num">{{ unit.critCount }}</td>
                              <td class="num">{{ formatNumber(unit.comboDamage) }}</td>
                              <td class="num">{{ unit.attackCount }}</td>
                              <td class="num">{{ unit.avgDamage }}</td>
                              <td class="num dps-val">{{ unit.dps }}</td>
                              <td class="num">{{ formatNumber(unit.damageReceived) }}</td>
                              <td>
                                  <span
                                      v-if="Object.keys(unit.skills).length"
                                      class="skill-toggle"
                                      @click="toggleSkillExpand(unit.name)"
                                  >{{ isSkillExpanded(unit.name) ? '收起' : Object.keys(unit.skills).length + '个' }}</span>
                                  <span v-else class="no-skill">-</span>
                              </td>
                          </tr>
                          <tr v-if="isSkillExpanded(unit.name) && Object.keys(unit.skills).length" :key="'enemy-skill-' + unit.name" class="skill-detail-row">
                              <td colspan="16">
                                  <table class="skill-sub-table">
                                      <thead>
                                          <tr>
                                              <th>技能名</th>
                                              <th>技能伤害</th>
                                              <th>连击伤害</th>
                                              <th>总伤害</th>
                                              <th>吸血</th>
                                              <th>恢复</th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          <tr v-for="(sk, skName) in unit.skills" :key="skName">
                                              <td class="skill-name-cell">{{ skName }}</td>
                                              <td class="num">{{ formatNumber(sk.damage - sk.comboDamage) }}</td>
                                              <td class="num">{{ formatNumber(sk.comboDamage) }}</td>
                                              <td class="num">{{ formatNumber(sk.damage) }}</td>
                                              <td class="num heal">{{ formatNumber(sk.lifeSteal) }}</td>
                                              <td class="num heal">{{ formatNumber(sk.healing) }}</td>
                                          </tr>
                                      </tbody>
                                  </table>
                              </td>
                          </tr>
                      </template>
                      <tr v-if="dpsStats.enemies.length === 0">
                          <td colspan="16" class="empty-cell">无敌方单位数据</td>
                      </tr>
                  </tbody>
              </table>
          </div>
      </div>

      <!-- 战斗单位显示 -->
      <div class="battle-units">
          <div class="stage-column battle-side battle-side-monster" v-if="monsterList.length > 0">
              <div class="section-title">
                  <Icon type="ios-skull" />
                  <span>怪物 ({{ monsterList.length }})</span>
              </div>
              <div class="stage-list single-line-scroll">
                  <Poptip
                      v-for="(item, index) in monsterList"
                      :key="'tower-monster-' + (item.id || index)"
                      trigger="hover"
                      placement="top-start"
                      word-wrap
                      transfer
                      popper-class="mud-pop"
                      class="mud-pop-trigger"
                  >
                      <div class="mud-line tower-unit-card" :class="{ defeated: getTowerMonsterCurrentHp(item) <= 0 }">
                          <span class="mud-name">
                              {{ item.name || '怪物' }}
                          </span>
                          <Progress
                              :percent="getUnitHpPercent(item, 'monster')"
                              :stroke-color="getHealthColor(getTowerMonsterCurrentHp(item), getTowerMonsterMaxHp(item))"
                              :show-info="false"
                              class="tower-unit-progress"
                          />
                          <span class="mud-value">{{ getTowerMonsterCurrentHp(item) }}/{{ getTowerMonsterMaxHp(item) }}</span>
                      </div>
                      <div slot="content" class="mud-detail">
                          <p>名称 {{ item.name || '怪物' }}</p>
                          <p>前缀 {{ item.prefix == 0 ? '无' : (item.prefix || '无') }}</p>
                          <p>生命 {{ getTowerMonsterCurrentHp(item) }}/{{ getTowerMonsterMaxHp(item) }}</p>
                          <p>攻击 {{ item.attackMin || item.attackMax || '--' }}</p>
                          <p>法力 {{ item.mana || '--' }}</p>
                          <p>物理防御 {{ item.defence || item.defense || '--' }}</p>
                          <p>法术防御 {{ item.magicDefense || '--' }}</p>
                          <p>速度 {{ item.speed || '--' }}</p>
                      </div>
                  </Poptip>
              </div> 
          </div>

          <div v-if="mergedAllies.length > 0" class="stage-column battle-side battle-side-ally">
              <div class="section-title">
                  <Icon type="ios-people" />
                  <span>我方单位 ({{ mergedAllies.length }})</span>
              </div>
              <div class="stage-list single-line-scroll">
                  <Poptip
                      v-for="(item, idx) in mergedAllies"
                      :key="item._key || ('tower-ally-' + idx)"
                      trigger="hover"
                      placement="top-start"
                      word-wrap
                      transfer
                      popper-class="mud-pop"
                      class="mud-pop-trigger"
                  >
                      <div class="mud-line tower-unit-card ally-unit-card" :class="{ defeated: getTowerAllyCurrentHp(item) <= 0 }">
                          <span class="mud-name">
                              <span class="mud-tag">{{ item._group }}</span>{{ getTowerAllyName(item) }}
                          </span>
                          <Progress
                              :percent="getUnitHpPercent(item, 'ally')"
                              :stroke-color="getHealthColor(getTowerAllyCurrentHp(item), getTowerAllyMaxHp(item))"
                              :show-info="false"
                              class="tower-unit-progress"
                          />
                          <span class="mud-value">{{ getTowerAllyCurrentHp(item) }}/{{ getTowerAllyMaxHp(item) }}</span>
                      </div>
                      <div slot="content" class="mud-detail">
                          <p>名称 {{ getTowerAllyName(item) }}</p>
                          <p>生命 {{ getTowerAllyCurrentHp(item) }}/{{ getTowerAllyMaxHp(item) }}</p>
                          <p>攻击 {{ item.attack || item.physicalAttack || item.auraAttack || '--' }}</p>
                          <p>法攻 {{ item.magicAttack || item.auraMagicAttack || '--' }}</p>
                          <p>物理防御 {{ item.defense || item.defence || item.auraDefence || '--' }}</p>
                          <p>法术防御 {{ item.magicDefense || item.auraMagicDefence || '--' }}</p>
                          <p>速度 {{ item.speed || item.movingSpeed || item.auraActionSpeed || '--' }}</p>
                      </div>
                  </Poptip>
              </div>
          </div>
      </div>

      <!-- 战斗记录区域 -->
      <div class="battle-recording">
          <div class="recording-header">
              <Icon type="ios-list" />
              <span>战斗记录</span>
              <Button
                  v-if="recording.length > 0"
                  @click="clearRecording"
                  size="small"
                  type="text"
                  icon="ios-trash"
              >
                  清空
              </Button>
          </div>
          <div class="recording-content" id="recording">
              <div v-if="recording.length === 0" class="empty-recording">
                  <Icon type="ios-chatboxes-outline" size="24" />
                  <p>暂无战斗记录</p>
              </div>
              <battle-process v-bind:recordings="recording"></battle-process>
          </div>
      </div>
  </div>
</template>

<script>
import battleProcess from "./battleProcess.vue";
import qs from "query-string";
import { sleep } from "../utils";

export default {
  components: { battleProcess },
  name: "lockDemonTower",
  data() {
      return {
          isMobile: false,
          showMapModal: false,
          mapList: [],
          mapid: '',
          mapName: '',
          battleTotal: false,
          expandedSkillUnits: {},
          monsterList: [],
          gameCharacterVO: [],
          gamePetVOList: [],
          recording: [],
          replaySpeed: 4,
          replaySpeedOptions: [2, 4, 'all'],
          towerResultText: '',
          towerRewardGranted: false,
          towerBattleDurationText: '',
          teamStatus: 1,
          isAutoBattle: false,
          autoBattleCountdown: 0,
          autoBattleTimer: null,
          autoBattleCountdownTimer: null,
      };
  },
  computed: {
      mergedAllies() {
          const chars = (this.gameCharacterVO || []).map((item, index) => {
              if (item) {
                  item._key = `char-${index}`;
                  item._group = '角色';
              }
              return item;
          });
          const pets = (this.gamePetVOList || []).map((item, index) => {
              if (item) {
                  item._key = `pet-${item.auraId || index}`;
                  item._group = '真灵';
              }
              return item;
          });
          return [...chars, ...pets];
      },
      dpsStats() {
          const records = this.recording.filter(r => r && r.type !== 'result');
          if (!records.length) {
              return { allies: [], enemies: [], totalRounds: 0, allyTotalDamage: 0, enemyTotalDamage: 0 };
          }
          const totalRounds = records.reduce((max, r) => Math.max(max, r.round || 0), 0);
          const unitMap = {};

          const initUnit = (name, identity) => {
              if (!unitMap[name]) {
                  unitMap[name] = {
                      name, identity,
                      totalDamage: 0, normalDamage: 0, skillDamage: 0,
                      counterDamage: 0, dotDamage: 0,
                      healing: 0, shield: 0, lifeSteal: 0,
                      critCount: 0, comboDamage: 0,
                      attackCount: 0, skillCount: 0,
                      damageReceived: 0,
                      skills: {}
                  };
              }
          };

          const monsterNames = new Set(this.monsterList.map(m => m && m.name).filter(Boolean));
          const allyNames = new Set(this.mergedAllies.map(a => (a && (a.name || a.auraName))).filter(Boolean));

          const detectIdentity = (name) => {
              if (monsterNames.has(name)) return 1;
              if (allyNames.has(name)) return 0;
              return -1;
          };

          const processRecord = (rec) => {
              const hurt = this.parseHurtValue(rec.hurtNum);
              const hurtCombo = this.parseHurtCombo(rec.hurtNum);
              const isDamage = rec.type !== 'gain';

              if (rec.attacker && hurt > 0) {
                  const u = unitMap[rec.attacker];
                  if (!u) return;

                  const ls = Number(rec.lifeStealAmount) || 0;

                  if (rec.isSkill == 1) {
                      u.skillCount++;
                      const sn = (rec.skillName || '未知').replace(/[\[\]]/g, '');
                      if (!u.skills[sn]) u.skills[sn] = { damage: 0, comboDamage: 0, lifeSteal: 0, healing: 0 };
                      if (rec.type === 'gain') {
                          u.skills[sn].healing += hurt;
                      } else {
                          u.skills[sn].damage += hurt;
                          if (hurtCombo > 0) u.skills[sn].comboDamage += hurtCombo;
                      }
                      if (ls > 0) u.skills[sn].lifeSteal += ls;
                  }

                  if (rec.type === 'gain') {
                      u.healing += hurt;
                  } else {
                      u.totalDamage += hurt;
                      u.attackCount++;

                      if (rec.hurtType === 4) {
                          u.counterDamage += hurt;
                      } else if (rec.hurtType === 3) {
                          u.dotDamage += hurt;
                      } else if (rec.isSkill == 1) {
                          u.skillDamage += hurt - hurtCombo;
                      } else {
                          u.normalDamage += hurt - hurtCombo;
                      }

                      if (hurtCombo > 0) u.comboDamage += hurtCombo;
                      if (rec.isCritical == 1) u.critCount++;
                      if (ls > 0) u.lifeSteal += ls;
                  }
              }

              if (rec.hinjured && hurt > 0 && isDamage) {
                  const t = unitMap[rec.hinjured];
                  if (t) t.damageReceived += hurt;
              }
          };

          records.forEach(r => {
              if (r.attacker) {
                  const id = detectIdentity(r.attacker);
                  if (id >= 0) initUnit(r.attacker, id);
              }
              if (r.hinjured) {
                  const id = detectIdentity(r.hinjured);
                  if (id >= 0) initUnit(r.hinjured, id);
              }
              // 初始化连击子目标
              const subs = Array.isArray(r.hinjuredConmbatList) ? r.hinjuredConmbatList : [];
              subs.forEach(sub => {
                  if (sub.attacker) {
                      const id = detectIdentity(sub.attacker);
                      if (id >= 0) initUnit(sub.attacker, id);
                  }
                  if (sub.hinjured) {
                      const id = detectIdentity(sub.hinjured);
                      if (id >= 0) initUnit(sub.hinjured, id);
                  }
              });
          });

          records.forEach(r => {
              processRecord(r);
              // 处理连击/多目标子伤害
              const subs = Array.isArray(r.hinjuredConmbatList) ? r.hinjuredConmbatList : [];
              subs.forEach(sub => processRecord(sub));
          });

          const toStats = u => ({
              ...u,
              avgDamage: u.attackCount > 0 ? Math.round(u.totalDamage / u.attackCount) : 0,
              dps: totalRounds > 0 ? (u.totalDamage / totalRounds).toFixed(1) : 0,
          });

          const allies = Object.values(unitMap).filter(u => u.identity === 0).map(toStats).sort((a, b) => b.totalDamage - a.totalDamage);
          const enemies = Object.values(unitMap).filter(u => u.identity !== 0).map(toStats).sort((a, b) => b.totalDamage - a.totalDamage);
          const allyTotalDamage = allies.reduce((s, u) => s + u.totalDamage, 0);
          const enemyTotalDamage = enemies.reduce((s, u) => s + u.totalDamage, 0);

          return { allies, enemies, totalRounds, allyTotalDamage, enemyTotalDamage };
      },
  },
  created() {
      this.checkMobile();
      window.addEventListener('resize', this.checkMobile);
      this.getMapList();
  },
  beforeDestroy() {
      window.removeEventListener('resize', this.checkMobile);
      this.stopAutoBattle();
  },
  updated() {
      // 战斗记录定位到底部
      const el = document.getElementById("recording");
      if (el) {
          const max = el.scrollHeight - el.clientHeight;
          if (max - el.scrollTop < 80) {
              el.scrollTop = el.scrollHeight;
          }
      }
  },
  methods: {
      checkMobile() {
          const responsive = this.$store && this.$store.state && this.$store.state.responsive
              ? this.$store.state.responsive
              : null;
          this.isMobile = responsive ? !!responsive.isMobile : window.innerWidth <= 768;
      },

      // 选择地图
      selectMap(item) {
          this.mapName = item.mapName;
          this.mapid = item.mapId;
          // 移动端选择后可以自动关闭弹窗，或者提示用户点击开始战斗
          if (this.isMobile) {
              this.$Message.info(`已选择 ${item.mapName}，点击"开始战斗"按钮开始挑战`);
          }
      },

      openWithMap(targetMap) {
          const targetMapId = targetMap && targetMap.mapId ? String(targetMap.mapId) : '';
          const targetMapName = targetMap && targetMap.mapName ? String(targetMap.mapName) : '';
          const autoStart = !!(targetMap && targetMap.autoStart);
          this.getMapList(targetMapId, targetMapName, autoStart);
      },

      // 获取健康值颜色
      getHealthColor(current, max) {
          const percent = (current / max) * 100;
          if (percent > 70) return '#19be6b';
          if (percent > 30) return '#ff9900';
          return '#ed4014';
      },
      getTowerMonsterCurrentHp(item) {
          return Math.max(Number(item && (item._currentHp || item.life)) || 0, 0);
      },
      getTowerMonsterMaxHp(item) {
          return Math.max(Number(item && (item._maxHp || item.formerLife || item.maxLife || item.life)) || 0, 0);
      },
      getTowerAllyCurrentHp(item) {
          return Math.max(Number((item && (item._currentHp || item.health || item.auraLife || item.life || item.formerHealth))) || 0, 0);
      },
      getTowerAllyMaxHp(item) {
          const currentHp = this.getTowerAllyCurrentHp(item);
          return Math.max(Number(item && (item._maxHp || item.maxLife || item.formerHealth || item.health || item.auraLife || item.life)) || 0, currentHp);
      },
      getTowerAllyName(item) {
          return (item && (item.name || item.auraName)) || '单位';
      },
      getUnitHpPercent(item, type) {
          const currentHp = type === 'monster'
              ? this.getTowerMonsterCurrentHp(item)
              : this.getTowerAllyCurrentHp(item);
          const maxHp = type === 'monster'
              ? this.getTowerMonsterMaxHp(item)
              : this.getTowerAllyMaxHp(item);
          if (!maxHp) {
              return 0;
          }
          return Number(Math.max(0, Math.min(100, (currentHp / maxHp) * 100)).toFixed(2));
      },
      resolveMaxHp(item, keys = []) {
          const values = [item && item.maxLife, ...(keys || []).map((key) => item && item[key])];
          const normalized = values
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value) && value > 0);
          return normalized.length ? Math.max(...normalized) : 0;
      },

      // 清空战斗记录
      clearRecording() {
          this.recording = [];
          this.battleTotal = false;
          this.towerResultText = '';
          this.towerRewardGranted = false;
          this.towerBattleDurationText = '';
          this.monsterList = [];
          this.gameCharacterVO = [];
          this.gamePetVOList = [];
      },

      getMapList(targetMapId, targetMapName, autoStart = false) {
          this.$http.get(`gameLockDemon/queryMapList?charaId=${this.getCookie("charaId")}`).then((res) => {
              this.mapList = res.data.data;
              if (targetMapId || targetMapName) {
                  const target = (this.mapList || []).find(item =>
                      String(item.mapId) === String(targetMapId)
                      || item.mapName === targetMapName
                      || item.mapName === `[宗门Boss] ${targetMapName}`
                  );
                  if (target) {
                      this.selectMap(target);
                      this.$Notice.success({ title: `已定位到 ${target.mapName}` });
                      if (autoStart) {
                          this.$nextTick(() => {
                              setTimeout(() => {
                                  this.startTheBattle();
                              }, 120);
                          });
                      }
                  } else {
                      this.$Notice.warning({ title: '未找到对应宗门Boss地图' });
                  }
              }
              if (!res.data.data) {
                  this.$Notice.success({title: res.data.msg});
              }
          }).catch((err) => {
              this.$Notice.warning({title: "获取锁妖塔地图失败,请联系管理员"});
          });
      },

      async startTheBattle() {
          if (!this.mapName) {
              this.$Message.warning('请先选择地图');
              if (this.isMobile) {
                  this.showMapModal = true;
              }
              return;
          }

          this.mapid = '';
          for (var i = 0; i < this.mapList.length; i++) {
              if (this.mapName == this.mapList[i].mapName) {
                  this.mapid = this.mapList[i].mapId;
              }
          }

          try {
              const res = await this.$http.post(
                  `gameLockDemon/checkMapGenMon?${qs.stringify({
                      charaId: this.getCookie("charaId"),
                      mapId: this.mapid,
                  })}`
              );

              if (res.data.msg != 'OK') {
                  this.$Message.warning(res.data.msg);
                  if (this.isAutoBattle) {
                      this.stopAutoBattle();
                  }
                  return
              }

              const body = res.data;

              if (body.status == 205) {
                  this.$Message.warning(body.msg);
                  if (this.isAutoBattle) {
                      this.stopAutoBattle();
                  }
                  return;
              }

              const combatResults = Array.isArray(body.data) ? body.data : [body.data];
              // 清空之前的战斗记录
              this.clearRecording();

              // 自动战斗时，用接口返回的战斗时长开始倒计时
              if (this.isAutoBattle && combatResults.length > 0) {
                  const lastResult = combatResults[combatResults.length - 1];
                  const duration = Number(lastResult && lastResult.dropTime) || 0;
                  if (duration > 0) {
                      this.startAutoBattleCountdown(duration, lastResult);
                  }
              }

              // 按顺序播报所有的战斗结果
              for (let i = 0; i < combatResults.length; i++) {
                  await this.boardcastCombatResult(combatResults[i]);
              }
          } catch (err) {
              this.$Message.error("战斗开始失败,请联系管理员");
              if (this.isAutoBattle) {
                  this.stopAutoBattle();
              }
          }
      },

      // 播报战斗返回的日志
      async boardcastCombatResult(result) {
          this.towerResultText = this.buildTowerResultText(result);
          this.towerRewardGranted = !!result.towerRewardGranted;
          this.towerBattleDurationText = this.formatTowerBattleDuration(result && result.dropTime);
          this.monsterList = (result.gameMonList || []).map((m) => ({
              ...m,
              maxLife: this.resolveMaxHp(m, ['formerLife', 'life', 'formerHealth', 'health']),
              _currentHp: this.resolveMaxHp(m, ['life', 'formerHealth', 'health']),
              _maxHp: this.resolveMaxHp(m, ['formerLife', 'life', 'formerHealth', 'health'])
          }));
          this.gameCharacterVO = result.gameCharacterVO ? [{
              ...result.gameCharacterVO,
              maxLife: this.resolveMaxHp(result.gameCharacterVO, ['formerHealth', 'health', 'life']),
              _currentHp: this.resolveMaxHp(result.gameCharacterVO, ['health', 'life', 'formerHealth']),
              _maxHp: this.resolveMaxHp(result.gameCharacterVO, ['formerHealth', 'health', 'life'])
          }] : [];
          const combatInfo = this.normalizeCombatInfo(result.combatInfo);
          const rawPetList = result.gamePetVOList || result.gamePetVO;
          const petSource = Array.isArray(rawPetList) ? rawPetList : (rawPetList ? [rawPetList] : []);
          const normalizedPets = petSource.map((pet) => ({
              ...pet,
              name: pet.name || pet.auraName,
              health: Number(pet.health || pet.auraLife || pet.life || pet.formerHealth) || 0,
              maxLife: this.resolveMaxHp(pet, ['formerHealth', 'auraLife', 'life', 'health']),
              _currentHp: this.resolveMaxHp(pet, ['auraLife', 'life', 'health', 'formerHealth']),
              _maxHp: this.resolveMaxHp(pet, ['formerHealth', 'auraLife', 'life', 'health'])
          }));
          this.gamePetVOList = normalizedPets.length > 0
              ? normalizedPets
              : this.deriveTowerPetsFromCombat(result, combatInfo);
          for (let i = 0; i < combatInfo.length; i++) {
              if (this.replaySpeed !== 'all') {
                  await sleep(Math.max(1500 / this.replaySpeed, 200));
              }
              const record = combatInfo[i];
              this.applyTowerRecord(record);
              this.addRecord(record);
          }
          // 额外增加一个战斗结果的消息记录
          if (result.reHealth <= 0) {
              this.addRecord({
                  type: "result",
                  result: "战斗失败",
              });
              this.battleTotal = true;
          } else {
              // 物品列表
              const goods = (result.gameItemsList || []).map((val) => {
                  return val.itemName;
              });
              const droppedGood = goods.length > 0;
              this.addRecord({
                  type: "result",
                  result: "战斗成功",
                  exp: "",
                  goods: "物品:" + (droppedGood ? goods.join() : "无"),
                  reHealth: result.reHealth,
                  reMana: result.reMana,
              });
              this.battleTotal = true;
              if (droppedGood) {
                  // 通知神通刷新检查
                  this.$bus.$emit("skillMsg", "锁妖塔发送过来的");
                  this.$bus.$emit("synthesisMsg", "锁妖塔发送过来的");
              }
          }
      },

      buildTowerResultText(result) {
          if (!result) return '';
          if (result.towerFirstClear && result.towerRewardGranted) {
              return result.requestDescribe || '本周首通，奖励已发放';
          }
          if (result.success) {
              return result.requestDescribe || '挑战成功，重复挑战无奖励';
          }
          return result.requestDescribe || '挑战失败';
      },
      formatTowerBattleDuration(seconds) {
          const total = Number(seconds) || 0;
          if (total <= 0) return '';
          if (total < 60) return `战斗时长 ${total} 秒`;
          const min = Math.floor(total / 60);
          const sec = total % 60;
          return `战斗时长 ${min} 分 ${sec} 秒`;
      },

      normalizeCombatInfo(combatInfo) {
          if (!combatInfo) {
              return [];
          }
          const list = Array.isArray(combatInfo) ? combatInfo : [combatInfo];
          return list.filter(Boolean).map((item, index) => this.normalizeCombatRecord(item, index));
      },

      normalizeCombatRecord(item, index = 0) {
          const hurtValue = item && (item.hurtNum || item.hurt || item.damage) || 0;
          return {
              ...(item || {}),
              round: (item && item.round) || index + 1,
              attacker: (item && (item.attacker || item.attackerName)) || '',
              hinjured: (item && (item.hinjured || item.targetName)) || '',
              hurt: hurtValue,
              hurtNum: hurtValue,
              type: item && item.hurtType == 5 ? 'gain' : 'combat',
              skillName: item && item.skillName,
              hitIndex: item && item.hitIndex,
              totalHits: item && item.totalHits,
              comboCount: item && item.comboCount,
              fixedComboCount: item && item.fixedComboCount,
              attElement: item && item.attElement,
              surplusHealth: item && item.surplusHealth,
              surplusShieldValue: item && item.surplusShieldValue,
              attackerSurplusHealth: item && item.attackerSurplusHealth,
              hpCost: item && item.hpCost,
              lifeStealAmount: item && item.lifeStealAmount,
              extraDamage: item && item.extraDamage,
              delayedHeal: item && item.delayedHeal,
              forceCrit: item && item.forceCrit,
              hasKillSpread: item && item.hasKillSpread,
              dotSettled: item && item.dotSettled,
              extraComboRatio: item && item.extraComboRatio,
              timeCategory: item && item.timeCategory,
              controlEffect: item && item.controlEffect,
              buffDesc: item && item.buffDesc,
              buffEffect: item && item.buffEffect,
              buffRoundEffect: item && item.buffRoundEffect,
              activeBuffList: item && item.activeBuffList,
              hinjuredConmbatList: this.normalizeSubCombatList(item && item.hinjuredConmbatList)
          };
      },

      normalizeSubCombatList(list) {
          if (!Array.isArray(list)) {
              return [];
          }
          return list.filter(Boolean).map((item, index) => ({
              ...(item || {}),
              round: (item && item.round) || index + 1,
              attacker: (item && (item.attacker || item.attackerName)) || '',
              hinjured: (item && (item.hinjured || item.targetName)) || '',
              hurt: (item && (item.hurt || item.hurtNum || item.damage)) || 0,
              hurtNum: (item && (item.hurtNum || item.hurt || item.damage)) || 0,
              skillName: item && item.skillName,
              surplusHealth: item && item.surplusHealth,
              attElement: item && item.attElement
          }));
      },

      deriveTowerPetsFromCombat(result, combatInfo) {
          const ownerName = result && result.gameCharacterVO && result.gameCharacterVO.name;
          const monsterNames = new Set((result && result.gameMonList || []).map(item => item && item.name).filter(Boolean));
          const petMap = new Map();

          const collectName = (name, hp) => {
              if (!name || name === ownerName || monsterNames.has(name)) {
                  return;
              }
              const numericHp = Number(hp);
              const current = petMap.get(name) || {
                  auraName: name,
                  name,
                  auraLife: 0,
                  health: 0,
                  formerHealth: 0,
                  maxLife: 0,
                  _currentHp: 0,
                  _maxHp: 0
              };
              if (Number.isFinite(numericHp) && numericHp >= 0) {
                  current.auraLife = Math.max(current.auraLife, numericHp);
                  current.health = Math.max(current.health, numericHp);
                  current.formerHealth = Math.max(current.formerHealth, numericHp);
                  current.maxLife = Math.max(current.maxLife, numericHp);
                  current._currentHp = Math.max(current._currentHp, numericHp);
                  current._maxHp = Math.max(current._maxHp, numericHp);
              }
              petMap.set(name, current);
          };

          (combatInfo || []).forEach((record) => {
              if (!record) return;
              if (record.identity === 0 || record.identity === '0') {
                  collectName(record.attacker, record.attackerSurplusHealth);
              }
              collectName(record.hinjured, record.surplusHealth);
              (record.hinjuredConmbatList || []).forEach((sub) => {
                  if (!sub) return;
                  if (sub.identity === 0 || sub.identity === '0') {
                      collectName(sub.attacker, sub.attackerSurplusHealth);
                  }
                  collectName(sub.hinjured, sub.surplusHealth);
              });
          });

          return Array.from(petMap.values());
      },

      findTowerUnitByName(name) {
          if (!name) return null;
          const monster = this.monsterList.find((item) => item.name === name);
          if (monster) {
              return { unit: monster, group: 'monster' };
          }
          const ally = this.mergedAllies.find((item) => (
              item.name === name || item.auraName === name || this.getTowerAllyName(item) === name
          ));
          if (ally) {
              return { unit: ally, group: 'ally' };
          }
          return null;
      },

      applyTowerHpToUnit(name, hp) {
          if (!name) return;
          const numericHp = Number(hp);
          if (!Number.isFinite(numericHp)) return;
          const targetEntry = this.findTowerUnitByName(name);
          if (!targetEntry) return;
          const targetHp = Math.max(numericHp, 0);
          if (targetEntry.group === 'monster') {
              this.$set(targetEntry.unit, '_currentHp', targetHp);
              this.$set(targetEntry.unit, 'life', targetHp);
              return;
          }
          const ally = targetEntry.unit;
          this.$set(ally, '_currentHp', targetHp);
          if (Object.prototype.hasOwnProperty.call(ally, 'health')) {
              this.$set(ally, 'health', targetHp);
          }
          if (Object.prototype.hasOwnProperty.call(ally, 'auraLife')) {
              this.$set(ally, 'auraLife', targetHp);
          }
          if (Object.prototype.hasOwnProperty.call(ally, 'life')) {
              this.$set(ally, 'life', targetHp);
          }
      },

      applyTowerRecord(record) {
          if (!record || record.type === 'result') {
              return;
          }
          if (record.hinjured && record.surplusHealth !== '' && record.surplusHealth !== null && record.surplusHealth !== undefined) {
              this.applyTowerHpToUnit(record.hinjured, record.surplusHealth);
          }
          const extraTargets = Array.isArray(record.hinjuredConmbatList) ? record.hinjuredConmbatList : [];
          extraTargets.forEach((target) => {
              if (!target) return;
              const targetName = target.hinjured || target.targetName || target.name;
              if (targetName && target.surplusHealth !== '' && target.surplusHealth !== null && target.surplusHealth !== undefined) {
                  this.applyTowerHpToUnit(targetName, target.surplusHealth);
              }
          });
          if (record.attacker && record.attackerSurplusHealth !== '' && record.attackerSurplusHealth !== null && record.attackerSurplusHealth !== undefined) {
              this.applyTowerHpToUnit(record.attacker, record.attackerSurplusHealth);
          }
      },

      addRecord(record) {
          this.recording.push(record);
          if (this.recording.length >= 200) {
              this.recording.splice(0, 100);
          }
      },
      setReplaySpeed(speed) {
          this.replaySpeed = speed;
      },

      // 开始自动战斗
      startAutoBattle() {
          this.isAutoBattle = true;
          this.startTheBattle();
      },

      // 停止自动战斗
      stopAutoBattle() {
          this.isAutoBattle = false;
          this.autoBattleCountdown = 0;
          if (this.autoBattleTimer) {
              clearTimeout(this.autoBattleTimer);
              this.autoBattleTimer = null;
          }
          if (this.autoBattleCountdownTimer) {
              clearInterval(this.autoBattleCountdownTimer);
              this.autoBattleCountdownTimer = null;
          }
      },

      // 开始自动战斗倒计时
      startAutoBattleCountdown(duration, result) {
          this.autoBattleCountdown = duration;
          if (this.autoBattleCountdownTimer) {
              clearInterval(this.autoBattleCountdownTimer);
          }
          const isSuccess = result && result.reHealth > 0;
          this.autoBattleCountdownTimer = setInterval(() => {
              this.autoBattleCountdown--;
              if (this.autoBattleCountdown <= 0) {
                  clearInterval(this.autoBattleCountdownTimer);
                  this.autoBattleCountdownTimer = null;
                  if (isSuccess && this.isAutoBattle) {
                      this.startTheBattle();
                  } else {
                      this.stopAutoBattle();
                  }
              }
          }, 1000);
      },

      formatNumber(num) {
          if (!num) return '0';
          if (num >= 10000) return (num / 10000).toFixed(1) + 'w';
          if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
          return num.toString();
      },
      toggleSkillExpand(unitName) {
          this.$set(this.expandedSkillUnits, unitName, !this.expandedSkillUnits[unitName]);
      },
      isSkillExpanded(unitName) {
          return !!this.expandedSkillUnits[unitName];
      },
      parseHurtValue(val) {
          if (val === undefined || val === null || val === '') return 0;
          if (typeof val === 'number') return Math.max(val, 0);
          const str = String(val).trim();
          if (str.includes(',')) {
              return str.split(',').reduce((sum, part) => {
                  const cleaned = part.replace(/[^\d.-]/g, '');
                  const n = Number(cleaned);
                  return sum + (Number.isFinite(n) ? Math.max(n, 0) : 0);
              }, 0);
          }
          const n = Number(str.replace(/[^\d.-]/g, ''));
          return Number.isFinite(n) ? Math.max(n, 0) : 0;
      },
      parseHurtCombo(val) {
          if (val === undefined || val === null || val === '') return 0;
          if (typeof val === 'number') return 0;
          const str = String(val).trim();
          if (!str.includes('⭐')) return 0;
          if (str.includes(',')) {
              return str.split(',').reduce((sum, part) => {
                  if (!part.includes('⭐')) return sum;
                  const cleaned = part.replace(/[^\d.-]/g, '');
                  const n = Number(cleaned);
                  return sum + (Number.isFinite(n) ? Math.max(n, 0) : 0);
              }, 0);
          }
          return 0;
      },
  },
};
</script>

<style scoped>
.lock-demon-tower {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
}

/* 头部样式 */
.header-section {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* 移动端头部 */
.mobile-header {
  margin-bottom: 15px;
}

.current-map {
  margin: 10px 0;
  text-align: center;
}

/* 地图选择模态框 */
.map-modal-content {
  padding: 15px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.map-grid {
  flex: 1;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  padding: 10px;
}

.map-item {
  padding: 15px;
  border: 2px solid #e8eaec;
  border-radius: 8px;
  background: white;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.map-item:hover {
  border-color: #2d8cf0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.1);
}

.map-item.selected {
  border-color: #19be6b;
  background: #f0f9ff;
}

.map-icon {
  margin-bottom: 10px;
  color: #2d8cf0;
}

.map-info h4 {
  margin: 5px 0;
  font-size: 14px;
  color: #333;
}

.map-info p {
  margin: 0;
  font-size: 12px;
  color: #666;
}

.selected-indicator {
  position: absolute;
  top: 5px;
  right: 5px;
}

.modal-footer {
  border-top: 1px solid #e8eaec;
  padding-top: 15px;
}

/* 战斗单位样式 */
.battle-units {
  margin-bottom: 20px;
  display: flex;
  flex-direction: row;
  gap: 14px;
  align-items: flex-start;
}

.stage-column {
  min-width: 0;
  flex: 1 1 0;
}

.battle-side-monster {
  align-items: flex-start;
}

.battle-side-ally {
  align-items: flex-end;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e8eaec;
  color: #333;
  font-weight: bold;
}

.stage-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding-bottom: 4px;
}

.single-line-scroll::-webkit-scrollbar {
  height: 6px;
}

.single-line-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.single-line-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.mud-pop-trigger {
  display: block;
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

.mud-line {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  min-height: 64px;
  padding: 8px 10px;
  border: 1px solid #d7dbe3;
  border-radius: 8px;
  background: rgba(45, 140, 240, 0.04);
  width: 100%;
  box-sizing: border-box;
}

.tower-unit-card {
  gap: 8px;
}

.tower-unit-progress {
  width: 100%;
}

.ally-unit-card {
  background: rgba(25, 190, 107, 0.05);
  border-color: rgba(25, 190, 107, 0.28);
}

.mud-line.defeated {
  opacity: 0.45;
}

.mud-name {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 13px;
  color: #2f3640;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mud-tag {
  flex-shrink: 0;
  font-size: 10px;
  color: #808695;
}

.mud-value {
  flex-shrink: 0;
  font-size: 12px;
  color: #808695;
  font-variant-numeric: tabular-nums;
}

.mud-detail p {
  margin: 4px 0;
}

/* 战斗统计 DPS */
.battle-stats {
  margin: 20px 0;
  padding: 15px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e8eaec;
  max-height: 60vh;
  overflow-y: auto;
}

.dps-summary-bar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 0 12px;
  border-bottom: 1px solid #e8eaec;
  margin-bottom: 12px;
}

.dps-section {
  margin-bottom: 16px;
}

.dps-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #515a6e;
  margin-bottom: 8px;
  padding-left: 4px;
  border-left: 3px solid #2d8cf0;
}

.dps-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: auto;
}

.dps-table thead th {
  background: rgba(45, 140, 240, 0.06);
  color: #515a6e;
  font-weight: 600;
  padding: 6px 4px;
  border-bottom: 2px solid #e8eaec;
  white-space: nowrap;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 1;
}

.dps-table tbody td {
  padding: 5px 4px;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
}

.dps-table .unit-name {
  text-align: left;
  font-weight: 600;
  color: #2d8cf0;
  white-space: nowrap;
  min-width: 80px;
}

.dps-table .unit-name.zero-dmg {
  color: #808695;
}

.dps-table .num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 50px;
}

.dps-table .num.heal {
  color: #19be6b;
}

.dps-table .num.dps-val {
  color: #ff9900;
  font-weight: 700;
}

.enemy-row .unit-name {
  color: #c62828 !important;
}

.skill-toggle {
  color: #2d8cf0;
  cursor: pointer;
  font-size: 11px;
  border-bottom: 1px dashed #2d8cf0;
}

.skill-toggle:hover {
  color: #57a3f3;
}

.no-skill {
  color: #c5c8ce;
  font-size: 11px;
}

.skill-detail-row td {
  padding: 0 !important;
  border-bottom: 2px solid #e8eaec !important;
}

.skill-sub-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin: 4px 0;
}

.skill-sub-table thead th {
  background: rgba(25, 190, 107, 0.08);
  color: #808695;
  font-weight: 500;
  padding: 3px 4px;
  font-size: 11px;
}

.skill-sub-table tbody td {
  padding: 3px 4px;
  border-bottom: 1px solid #f5f5f5;
}

.skill-name-cell {
  text-align: left !important;
  color: #19be6b;
  padding-left: 12px !important;
}

.empty-cell {
  color: #c5c8ce;
  font-style: italic;
  padding: 12px !important;
}

/* 战斗记录 */
.tower-status-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0;
}

.battle-recording {
  background: white;
  border-radius: 8px;
  border: 1px solid #e8eaec;
  overflow: hidden;
}

.recording-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaec;
  font-weight: bold;
  color: #333;
}
.recording-content {
  max-height: 200px;
  overflow-y: auto;
  padding: 15px;
}

.empty-recording {
  text-align: center;
  color: #999;
  padding: 20px;
}

.empty-recording p {
  margin-top: 10px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .battle-units {
      flex-direction: column;
  }

  .lock-demon-tower {
      padding: 10px;
  }

  .mud-pop-trigger {
      width: 100%;
      min-width: 0;
  }

  .mud-line {
      min-height: 58px;
      padding: 8px;
  }

  .mud-name {
      font-size: 12px;
  }

  .map-grid {
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 8px;
  }

  .recording-content {
      max-height: 150px;
      font-size: 12px;
  }

  /* 表格在移动端滚动 */
  .battle-stats {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
  }

  .battle-stats >>> .ivu-table {
      min-width: 520px;
  }

  .battle-stats >>> .ivu-table-cell {
      white-space: nowrap;
      padding-left: 8px;
      padding-right: 8px;
  }
}

/* 滚动条样式 */
.recording-content::-webkit-scrollbar {
  width: 6px;
}

.recording-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.recording-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.recording-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
      opacity: 0;
      transform: translateY(10px);
  }
  to {
      opacity: 1;
      transform: translateY(0);
  }
}

.mud-pop-trigger {
  animation: fadeIn 0.3s ease-out;
}
</style>



// WEBPACK FOOTER //
// src/components/lockDemonTower.vue